// ============================================
// MODALNUEVOCLIENTEEXPEDIENTE.JS
// ============================================
// Wizard de 4 pasos para crear cliente nuevo desde el modal de Expediente.
// Encadena POSTs a endpoints existentes (NO modifica módulo Clientes):
//   POST /api/clientes/grupos
//   POST /api/clientes/razones-sociales
//   POST /api/clientes/sucursales
//   POST /api/clientes/personas
//   POST /api/clientes/roles-contacto
// Si el usuario elige "PERSONA" en el paso 1, salta directo al paso 4.

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { API_BASE_CLI } from './helpersClientes';
import { authHeaders } from '../utils/storage';

const API = API_BASE_CLI;

const COLOR_AZUL    = '#185FA5';
const COLOR_LIMA    = '#94b803';
const COLOR_OSCURO  = '#270f2b';
const COLOR_BORDE   = '#e5e7eb';
const COLOR_GRIS    = '#5e666e';
const COLOR_GRIS_CL = '#9aa0a6';

const FF = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const TIPOS_ROL = [
  { v: 'encargado_sucursal', label: 'Encargado de sucursal' },
  { v: 'gerente',            label: 'Gerente' },
  { v: 'administrador',      label: 'Administrador / Tomador de decisión' },
  { v: 'propietario',        label: 'Propietario' },
  { v: 'contacto',           label: 'Contacto general' },
];

function valorVacio() {
  return {
    cliente_id: null,
    cliente_grupo_id: null,
    cliente_razon_social_id: null,
    cliente_sucursal_id: null,
    cliente_persona_id: null,
    _resumen: '',
    _detalle: null,
  };
}

export default function ModalNuevoClienteExpediente({
  abierto, onCerrar, onCreado, esCelular, nombrePrefilled,
}) {
  // ===== STATE =====
  const [paso, setPaso] = useState(1);
  const [tipoCliente, setTipoCliente] = useState(null); // 'empresa' | 'persona'

  // Paso 2 — empresa
  const [agrupacion, setAgrupacion] = useState(null);   // 'independiente' | 'grupo'
  const [grupoExistente, setGrupoExistente] = useState(null); // {id, nombre} si seleccionado
  const [grupoNombreNuevo, setGrupoNombreNuevo] = useState('');
  const [busquedaGrupo, setBusquedaGrupo] = useState('');
  const [resultadosGrupo, setResultadosGrupo] = useState([]);
  const [empresa, setEmpresa] = useState({
    razon_social: '',
    rfc: '',
    regimen_sat: '',
  });
  const [rfcDuplicado, setRfcDuplicado] = useState(null); // razón social existente con ese RFC

  // Paso 3 — sucursal
  const [sucursal, setSucursal] = useState({
    nombre_interno: '',
    direccion: '',
    ciudad: '',
    estado_republica: '',
    codigo_postal: '',
    telefono: '',
    notas_acceso: '',
  });
  const [saltarSucursal, setSaltarSucursal] = useState(false);

  // Paso 4 — persona
  const [persona, setPersona] = useState({
    nombre_completo: '',
    celular: '',
    email: '',
    tipo_rol: 'contacto',
  });
  const [celularDuplicado, setCelularDuplicado] = useState(null);
  const [saltarPersona, setSaltarPersona] = useState(false);

  // Final
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState(null);

  // ===== EFFECTS =====

  // Reset al abrir + prefill nombre desde el typeahead
  useEffect(() => {
    if (!abierto) return;
    setPaso(1);
    setTipoCliente(null);
    setAgrupacion(null);
    setGrupoExistente(null);
    setGrupoNombreNuevo('');
    setBusquedaGrupo('');
    setResultadosGrupo([]);
    const prefill = (nombrePrefilled || '').trim();
    setEmpresa({ razon_social: prefill, rfc: '', regimen_sat: '' });
    setRfcDuplicado(null);
    setSucursal({
      nombre_interno: '', direccion: '', ciudad: '', estado_republica: '',
      codigo_postal: '', telefono: '', notas_acceso: '',
    });
    setSaltarSucursal(false);
    setPersona({
      nombre_completo: tipoCliente === 'persona' ? prefill : '',
      celular: '', email: '', tipo_rol: 'contacto',
    });
    setCelularDuplicado(null);
    setSaltarPersona(false);
    setCreando(false);
    setError(null);
  }, [abierto]); // eslint-disable-line react-hooks/exhaustive-deps

  // ESC cierra + body lock
  useEffect(() => {
    if (!abierto) return;
    const onKey = (e) => { if (e.key === 'Escape' && !creando) onCerrar(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [abierto, creando, onCerrar]);

  // Paso 2 — typeahead grupos
  useEffect(() => {
    if (paso !== 2 || agrupacion !== 'grupo' || busquedaGrupo.trim().length < 2) {
      setResultadosGrupo([]);
      return;
    }
    let activo = true;
    const t = setTimeout(() => {
      fetch(`${API}/api/clientes/buscar?q=${encodeURIComponent(busquedaGrupo.trim())}&tipo=grupos`, { headers: authHeaders() })
        .then(r => r.json())
        .then(r => {
          if (!activo) return;
          setResultadosGrupo(r.ok ? (r.resultados || []).filter(x => x.tipo === 'grupo') : []);
        })
        .catch(() => activo && setResultadosGrupo([]));
    }, 300);
    return () => { activo = false; clearTimeout(t); };
  }, [busquedaGrupo, paso, agrupacion]);

  // Paso 2 — dedup por RFC
  useEffect(() => {
    if (paso !== 2) return;
    const rfc = (empresa.rfc || '').trim().toUpperCase();
    if (rfc.length < 12) { setRfcDuplicado(null); return; }
    let activo = true;
    const t = setTimeout(() => {
      fetch(`${API}/api/clientes/razones-sociales/buscar-por-rfc?rfc=${encodeURIComponent(rfc)}`, { headers: authHeaders() })
        .then(r => r.json())
        .then(r => {
          if (!activo) return;
          if (r.ok && r.razon_social) setRfcDuplicado(r.razon_social);
          else setRfcDuplicado(null);
        })
        .catch(() => activo && setRfcDuplicado(null));
    }, 400);
    return () => { activo = false; clearTimeout(t); };
  }, [empresa.rfc, paso]);

  // Paso 4 — dedup por celular
  useEffect(() => {
    if (paso !== 4 || saltarPersona) return;
    const tel = (persona.celular || '').replace(/\D/g, '');
    if (tel.length < 10) { setCelularDuplicado(null); return; }
    let activo = true;
    const t = setTimeout(() => {
      fetch(`${API}/api/clientes/personas/buscar-por-celular?celular=${encodeURIComponent(tel)}`, { headers: authHeaders() })
        .then(r => r.json())
        .then(r => {
          if (!activo) return;
          if (r.ok && Array.isArray(r.personas) && r.personas.length > 0) {
            setCelularDuplicado(r.personas[0]);
          } else {
            setCelularDuplicado(null);
          }
        })
        .catch(() => activo && setCelularDuplicado(null));
    }, 400);
    return () => { activo = false; clearTimeout(t); };
  }, [persona.celular, paso, saltarPersona]);

  if (!abierto) return null;

  // ===== HELPERS =====
  const totalPasos = tipoCliente === 'persona' ? 1 : 4;
  const indicePaso = tipoCliente === 'persona' ? 1 : paso;

  const puedeAvanzarPaso2 = !!(
    agrupacion &&
    empresa.razon_social.trim() &&
    (agrupacion === 'independiente' || grupoExistente || grupoNombreNuevo.trim())
  );
  const puedeAvanzarPaso3 = saltarSucursal || !!sucursal.nombre_interno.trim();
  const puedeFinalizar = (() => {
    if (tipoCliente === 'persona') {
      return !!persona.nombre_completo.trim();
    }
    // Empresa
    if (saltarPersona) return true;
    return !!persona.nombre_completo.trim();
  })();

  // ===== ACCIONES =====

  const reiniciar = () => {
    setPaso(1); setTipoCliente(null); setError(null);
  };

  const elegirTipo = (t) => {
    setTipoCliente(t);
    if (t === 'persona') {
      setPaso(4);
      setSaltarPersona(false);
      setPersona(p => ({ ...p, nombre_completo: nombrePrefilled || '' }));
    } else {
      setPaso(2);
    }
  };

  const finalizar = async () => {
    setCreando(true);
    setError(null);
    try {
      let detalle = { grupo: null, razon_social: null, sucursal: null, persona: null };

      if (tipoCliente === 'empresa') {
        // 1) Grupo
        let grupoId = null;
        if (grupoExistente?.id) {
          grupoId = grupoExistente.id;
          detalle.grupo = { id: grupoExistente.id, nombre: grupoExistente.etiqueta || grupoExistente.nombre };
        } else {
          const nombreGrupo = agrupacion === 'independiente'
            ? empresa.razon_social.trim()
            : grupoNombreNuevo.trim();
          const r = await fetch(`${API}/api/clientes/grupos`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({ nombre: nombreGrupo }),
          }).then(x => x.json());
          if (!r.ok) throw new Error('Grupo: ' + (r.error || 'no se pudo crear'));
          grupoId = r.grupo.id;
          detalle.grupo = r.grupo;
        }

        // 2) Razón social
        const rsBody = {
          razon_social: empresa.razon_social.trim(),
          grupo_id: grupoId,
          rfc: empresa.rfc.trim() || null,
          regimen_sat: empresa.regimen_sat.trim() || null,
        };
        const rs = await fetch(`${API}/api/clientes/razones-sociales`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(rsBody),
        }).then(x => x.json());
        if (!rs.ok) throw new Error('Razón social: ' + (rs.error || 'no se pudo crear'));
        detalle.razon_social = rs.razon_social;
        const razonSocialId = rs.razon_social.id;

        // 3) Sucursal
        let sucursalId = null;
        if (!saltarSucursal && sucursal.nombre_interno.trim()) {
          const su = await fetch(`${API}/api/clientes/sucursales`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({
              razon_social_id: razonSocialId,
              nombre_interno: sucursal.nombre_interno.trim(),
              direccion: sucursal.direccion.trim() || null,
              ciudad: sucursal.ciudad.trim() || null,
              estado_republica: sucursal.estado_republica.trim() || null,
              codigo_postal: sucursal.codigo_postal.trim() || null,
              telefono: sucursal.telefono.trim() || null,
              notas_acceso: sucursal.notas_acceso.trim() || null,
            }),
          }).then(x => x.json());
          if (!su.ok) throw new Error('Sucursal: ' + (su.error || 'no se pudo crear'));
          detalle.sucursal = su.sucursal;
          sucursalId = su.sucursal.id;
        }

        // 4) Persona + rol
        if (!saltarPersona && persona.nombre_completo.trim()) {
          const per = await fetch(`${API}/api/clientes/personas`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({
              nombre_completo: persona.nombre_completo.trim(),
              celular: persona.celular.trim() || null,
              email: persona.email.trim() || null,
            }),
          }).then(x => x.json());
          if (!per.ok) throw new Error('Persona: ' + (per.error || 'no se pudo crear'));
          detalle.persona = per.persona;

          // Vincula rol al alcance más específico disponible
          const alcanceTipo = sucursalId ? 'sucursal' : 'razon_social';
          const alcanceId   = sucursalId ? sucursalId : razonSocialId;
          try {
            await fetch(`${API}/api/clientes/roles-contacto`, {
              method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
              body: JSON.stringify({
                persona_id: per.persona.id,
                tipo: persona.tipo_rol || 'contacto',
                alcance_tipo: alcanceTipo,
                alcance_id: alcanceId,
              }),
            });
          } catch { /* best-effort: el rol no es bloqueante */ }
        }
      } else {
        // tipoCliente === 'persona' (camino corto)
        const per = await fetch(`${API}/api/clientes/personas`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({
            nombre_completo: persona.nombre_completo.trim(),
            celular: persona.celular.trim() || null,
            email: persona.email.trim() || null,
            es_cliente_persona_fisica: true,
          }),
        }).then(x => x.json());
        if (!per.ok) throw new Error('Persona: ' + (per.error || 'no se pudo crear'));
        detalle.persona = per.persona;
      }

      // Construir resultado para el padre
      const resumen = [
        detalle.grupo?.nombre,
        detalle.razon_social?.razon_social,
        detalle.sucursal?.nombre_interno,
        detalle.persona?.nombre_completo,
      ].filter(Boolean).join(' › ');

      const resultado = {
        ...valorVacio(),
        cliente_grupo_id: detalle.grupo?.id || null,
        cliente_razon_social_id: detalle.razon_social?.id || null,
        cliente_sucursal_id: detalle.sucursal?.id || null,
        cliente_persona_id: detalle.persona?.id || null,
        _resumen: resumen,
        _detalle: detalle,
      };
      onCreado(resultado);
    } catch (e) {
      setError(e.message || 'Error inesperado al crear cliente');
    } finally {
      setCreando(false);
    }
  };

  // ===== RENDER =====
  const contenido = (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)',
      zIndex: 5500, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: esCelular ? 0 : 20, fontFamily: FF,
    }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: esCelular ? '14px 14px 0 0' : 16,
          width: '100%', maxWidth: 640,
          maxHeight: esCelular ? '92vh' : '90vh',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          alignSelf: esCelular ? 'flex-end' : 'center',
          boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${COLOR_BORDE}`,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10,
        }}>
          <div>
            <h2 style={{
              margin: 0, color: COLOR_OSCURO, fontSize: 18, fontWeight: 700, fontFamily: FF,
            }}>Crear cliente nuevo</h2>
            <div style={{ marginTop: 4, fontSize: 12, color: COLOR_GRIS_CL }}>
              {tipoCliente === 'persona'
                ? 'Persona física'
                : tipoCliente === 'empresa'
                  ? `Paso ${indicePaso} de ${totalPasos}`
                  : 'Selecciona el tipo de cliente'}
            </div>
          </div>
          <button
            onClick={onCerrar}
            disabled={creando}
            aria-label="Cerrar"
            style={{
              background: 'transparent', border: 'none',
              cursor: creando ? 'not-allowed' : 'pointer',
              color: COLOR_GRIS_CL, fontSize: 24, padding: 4, fontFamily: FF,
              opacity: creando ? 0.5 : 1,
            }}
          >×</button>
        </div>

        {/* Stepper */}
        {tipoCliente === 'empresa' && (
          <Stepper paso={paso} esCelular={esCelular} />
        )}

        {/* Cuerpo */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: 22,
          display: 'flex', flexDirection: 'column', gap: 16,
        }}>
          {paso === 1 && <Paso1 onElegir={elegirTipo} esCelular={esCelular} />}
          {paso === 2 && tipoCliente === 'empresa' && (
            <Paso2Empresa
              agrupacion={agrupacion} setAgrupacion={setAgrupacion}
              grupoExistente={grupoExistente} setGrupoExistente={setGrupoExistente}
              grupoNombreNuevo={grupoNombreNuevo} setGrupoNombreNuevo={setGrupoNombreNuevo}
              busquedaGrupo={busquedaGrupo} setBusquedaGrupo={setBusquedaGrupo}
              resultadosGrupo={resultadosGrupo}
              empresa={empresa} setEmpresa={setEmpresa}
              rfcDuplicado={rfcDuplicado}
              esCelular={esCelular}
            />
          )}
          {paso === 3 && tipoCliente === 'empresa' && (
            <Paso3Sucursal
              sucursal={sucursal} setSucursal={setSucursal}
              saltarSucursal={saltarSucursal} setSaltarSucursal={setSaltarSucursal}
              esCelular={esCelular}
            />
          )}
          {paso === 4 && (
            <Paso4Persona
              persona={persona} setPersona={setPersona}
              celularDuplicado={celularDuplicado}
              saltarPersona={tipoCliente === 'empresa' ? saltarPersona : false}
              setSaltarPersona={tipoCliente === 'empresa' ? setSaltarPersona : null}
              esCelular={esCelular}
            />
          )}
          {error && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
              padding: 12, borderRadius: 8, fontSize: 13,
            }}>{error}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: 18, borderTop: `1px solid ${COLOR_BORDE}`, background: '#fafafa',
          display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
        }}>
          <button
            onClick={paso > 1 && !creando ? () => {
              if (paso === 4 && tipoCliente === 'empresa') setPaso(3);
              else if (paso === 4 && tipoCliente === 'persona') reiniciar();
              else if (paso === 3) setPaso(2);
              else if (paso === 2) reiniciar();
            } : onCerrar}
            disabled={creando}
            style={btnSecundario}
          >{paso === 1 ? 'Cancelar' : '← Atrás'}</button>

          <div style={{ display: 'flex', gap: 10 }}>
            {paso === 2 && (
              <button
                onClick={() => setPaso(3)}
                disabled={!puedeAvanzarPaso2 || creando}
                style={{ ...btnPrimario, opacity: puedeAvanzarPaso2 ? 1 : 0.5 }}
              >Siguiente →</button>
            )}
            {paso === 3 && (
              <button
                onClick={() => setPaso(4)}
                disabled={!puedeAvanzarPaso3 || creando}
                style={{ ...btnPrimario, opacity: puedeAvanzarPaso3 ? 1 : 0.5 }}
              >Siguiente →</button>
            )}
            {paso === 4 && (
              <button
                onClick={finalizar}
                disabled={!puedeFinalizar || creando}
                style={{ ...btnPrimario, opacity: (puedeFinalizar && !creando) ? 1 : 0.5 }}
              >{creando ? '⟳ Creando…' : 'Crear cliente'}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(contenido, document.body);
}

// ============================================
// STEPPER
// ============================================
function Stepper({ paso, esCelular }) {
  const pasos = [
    { n: 1, label: 'Tipo' },
    { n: 2, label: 'Empresa' },
    { n: 3, label: 'Sucursal' },
    { n: 4, label: 'Contacto' },
  ];
  return (
    <div style={{
      padding: '12px 22px', background: '#f9fafb', borderBottom: `1px solid ${COLOR_BORDE}`,
      display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
    }}>
      {pasos.map((p, i) => {
        const completado = paso > p.n;
        const activo = paso === p.n;
        const color = completado ? COLOR_LIMA : (activo ? COLOR_AZUL : COLOR_GRIS_CL);
        return (
          <React.Fragment key={p.n}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                background: completado || activo ? color : '#fff',
                color: completado || activo ? '#fff' : COLOR_GRIS_CL,
                border: `2px solid ${color}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700,
              }}>{completado ? '✓' : p.n}</div>
              {!esCelular && (
                <span style={{
                  color: activo ? COLOR_OSCURO : color,
                  fontWeight: activo ? 700 : 500,
                }}>{p.label}</span>
              )}
            </div>
            {i < pasos.length - 1 && (
              <div style={{
                flex: esCelular ? 1 : '0 0 18px', height: 2,
                background: completado ? COLOR_LIMA : COLOR_BORDE,
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ============================================
// PASO 1 — Tipo
// ============================================
function Paso1({ onElegir, esCelular }) {
  return (
    <div>
      <p style={{
        margin: '0 0 18px', fontSize: 14, color: COLOR_OSCURO, fontWeight: 600,
      }}>¿Qué tipo de cliente vas a registrar?</p>
      <div style={{
        display: 'grid', gap: 12,
        gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr',
      }}>
        <CardTipo
          icono="🏢" titulo="Empresa"
          descripcion="Negocio, corporativo o institución con grupo, razón social y sucursal"
          onClick={() => onElegir('empresa')}
        />
        <CardTipo
          icono="👤" titulo="Persona"
          descripcion="Cliente persona física directa, sin estructura corporativa"
          onClick={() => onElegir('persona')}
        />
      </div>
    </div>
  );
}

function CardTipo({ icono, titulo, descripcion, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: '#fff',
        border: `2px solid ${hover ? COLOR_AZUL : COLOR_BORDE}`,
        borderRadius: 12, padding: 22, cursor: 'pointer',
        textAlign: 'left', fontFamily: FF,
        transition: 'border-color 0.2s, transform 0.15s',
        transform: hover ? 'translateY(-1px)' : 'none',
      }}
    >
      <div style={{ fontSize: 36, marginBottom: 10 }}>{icono}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 4 }}>
        {titulo}
      </div>
      <div style={{ fontSize: 12, color: COLOR_GRIS, lineHeight: 1.4 }}>
        {descripcion}
      </div>
    </button>
  );
}

// ============================================
// PASO 2 — Empresa (grupo + razón social)
// ============================================
function Paso2Empresa({
  agrupacion, setAgrupacion,
  grupoExistente, setGrupoExistente,
  grupoNombreNuevo, setGrupoNombreNuevo,
  busquedaGrupo, setBusquedaGrupo,
  resultadosGrupo,
  empresa, setEmpresa,
  rfcDuplicado,
  esCelular,
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Agrupación */}
      <div>
        <p style={{ fontSize: 13, color: COLOR_OSCURO, fontWeight: 600, margin: '0 0 10px' }}>
          ¿La empresa pertenece a un grupo (cliente) corporativo o es independiente?
        </p>
        <div style={{ display: 'grid', gap: 8, gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr' }}>
          <BotonOpcion
            sel={agrupacion === 'independiente'}
            onClick={() => { setAgrupacion('independiente'); setGrupoExistente(null); }}
          >
            <div style={{ fontSize: 18, marginBottom: 4 }}>🏪</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Es independiente</div>
            <div style={{ fontSize: 11, color: COLOR_GRIS_CL }}>Una sola empresa, sin matriz</div>
          </BotonOpcion>
          <BotonOpcion
            sel={agrupacion === 'grupo'}
            onClick={() => setAgrupacion('grupo')}
          >
            <div style={{ fontSize: 18, marginBottom: 4 }}>📁</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Pertenece a un grupo (cliente)</div>
            <div style={{ fontSize: 11, color: COLOR_GRIS_CL }}>Tiene matriz o más empresas</div>
          </BotonOpcion>
        </div>
      </div>

      {/* Selector de grupo (solo si pertenece a grupo) */}
      {agrupacion === 'grupo' && (
        <div>
          <p style={{ fontSize: 12, color: COLOR_GRIS, fontWeight: 600, margin: '0 0 6px' }}>
            Grupo (cliente) *
          </p>
          {grupoExistente ? (
            <div style={{
              padding: 10, border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
              background: '#eef4fc', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO }}>
                📁 {grupoExistente.etiqueta || grupoExistente.nombre}
              </span>
              <button type="button" onClick={() => setGrupoExistente(null)} style={btnLink}>
                Cambiar
              </button>
            </div>
          ) : (
            <>
              <input
                type="text"
                value={busquedaGrupo}
                onChange={(e) => setBusquedaGrupo(e.target.value)}
                placeholder="Buscar grupo (cliente) existente o crear nuevo…"
                style={inputStyle}
              />
              {busquedaGrupo.trim().length >= 2 && resultadosGrupo.length > 0 && (
                <div style={{
                  marginTop: 4, border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
                  background: '#fff', maxHeight: 180, overflowY: 'auto',
                }}>
                  {resultadosGrupo.map(r => (
                    <div
                      key={r.id}
                      onClick={() => {
                        setGrupoExistente(r);
                        setBusquedaGrupo('');
                        setGrupoNombreNuevo('');
                      }}
                      style={{
                        padding: '8px 12px', cursor: 'pointer',
                        borderBottom: `1px solid ${COLOR_BORDE}`,
                        fontSize: 13, color: COLOR_OSCURO,
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f4'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                    >📁 {r.etiqueta}</div>
                  ))}
                </div>
              )}
              <div style={{ marginTop: 8, fontSize: 12, color: COLOR_GRIS_CL }}>
                ¿No existe? Escribe el nombre del grupo nuevo aquí:
              </div>
              <input
                type="text"
                value={grupoNombreNuevo}
                onChange={(e) => setGrupoNombreNuevo(e.target.value)}
                placeholder="Ej: Grupo Bodegas Alianza"
                style={inputStyle}
              />
            </>
          )}
        </div>
      )}

      {/* Razón social */}
      <div>
        <p style={{ fontSize: 12, color: COLOR_GRIS, fontWeight: 600, margin: '0 0 6px' }}>
          Razón social *
        </p>
        <input
          type="text"
          value={empresa.razon_social}
          onChange={(e) => setEmpresa({ ...empresa, razon_social: e.target.value })}
          placeholder="Ej: Bodegas Alianza S.A. de C.V."
          style={inputStyle}
        />
      </div>

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr' }}>
        <div>
          <p style={{ fontSize: 12, color: COLOR_GRIS, fontWeight: 600, margin: '0 0 6px' }}>RFC (opcional)</p>
          <input
            type="text"
            value={empresa.rfc}
            onChange={(e) => setEmpresa({ ...empresa, rfc: e.target.value.toUpperCase() })}
            placeholder="XAXX010101000"
            maxLength={13}
            style={inputStyle}
          />
        </div>
        <div>
          <p style={{ fontSize: 12, color: COLOR_GRIS, fontWeight: 600, margin: '0 0 6px' }}>Régimen SAT (opcional)</p>
          <input
            type="text"
            value={empresa.regimen_sat}
            onChange={(e) => setEmpresa({ ...empresa, regimen_sat: e.target.value })}
            placeholder="Ej: Régimen Simplificado de Confianza"
            style={inputStyle}
          />
        </div>
      </div>

      {rfcDuplicado && (
        <div style={cajaAlerta}>
          <span style={{ fontSize: 16, marginRight: 8 }}>⚠️</span>
          Ya existe una razón social con este RFC: <strong>{rfcDuplicado.razon_social}</strong>.
          Considera buscarla y reutilizarla en lugar de crear duplicado.
        </div>
      )}
    </div>
  );
}

// ============================================
// PASO 3 — Sucursal
// ============================================
function Paso3Sucursal({ sucursal, setSucursal, saltarSucursal, setSaltarSucursal, esCelular }) {
  if (saltarSucursal) {
    return (
      <div style={cajaInfo}>
        <span style={{ fontSize: 16, marginRight: 8 }}>ℹ️</span>
        Vas a registrar el cliente sin sucursal por ahora. Se podrá agregar después.
        <button
          type="button"
          onClick={() => setSaltarSucursal(false)}
          style={{ ...btnLink, marginLeft: 8 }}
        >Deshacer</button>
      </div>
    );
  }
  const k = (campo) => (e) => setSucursal({ ...sucursal, [campo]: e.target.value });
  const algoEscrito = Object.values(sucursal).some(v => String(v).trim());
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ fontSize: 13, color: COLOR_OSCURO, fontWeight: 600, margin: 0 }}>
        Datos de la sucursal o local del cliente
      </p>

      <div>
        <p style={labelMini}>Nombre interno *</p>
        <input
          type="text" value={sucursal.nombre_interno} onChange={k('nombre_interno')}
          placeholder='Ej: "Matriz", "Tlalpan", "Bodega CDMX"'
          style={inputStyle}
        />
      </div>

      <div>
        <p style={labelMini}>Dirección</p>
        <input
          type="text" value={sucursal.direccion} onChange={k('direccion')}
          placeholder="Calle, número, colonia"
          style={inputStyle}
        />
      </div>

      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: esCelular ? '1fr 1fr' : '2fr 2fr 1fr' }}>
        <div>
          <p style={labelMini}>Ciudad</p>
          <input type="text" value={sucursal.ciudad} onChange={k('ciudad')} style={inputStyle} />
        </div>
        <div>
          <p style={labelMini}>Estado</p>
          <input type="text" value={sucursal.estado_republica} onChange={k('estado_republica')} style={inputStyle} />
        </div>
        <div>
          <p style={labelMini}>C.P.</p>
          <input type="text" value={sucursal.codigo_postal} onChange={k('codigo_postal')} maxLength={6} style={inputStyle} />
        </div>
      </div>

      <div>
        <p style={labelMini}>Teléfono (opcional)</p>
        <input type="tel" value={sucursal.telefono} onChange={k('telefono')} style={inputStyle} />
      </div>

      <div>
        <p style={labelMini}>Notas de acceso (opcional)</p>
        <textarea
          value={sucursal.notas_acceso} onChange={k('notas_acceso')} rows={2}
          placeholder='Ej: "preguntar por el encargado en recepción"'
          style={{ ...inputStyle, resize: 'vertical', minHeight: 50, fontFamily: FF }}
        />
      </div>

      <button
        type="button"
        onClick={() => setSaltarSucursal(true)}
        disabled={algoEscrito}
        style={{
          ...btnLink, marginTop: 4, alignSelf: 'flex-start',
          opacity: algoEscrito ? 0.4 : 1,
          cursor: algoEscrito ? 'not-allowed' : 'pointer',
        }}
        title={algoEscrito ? 'Borra los campos para saltar' : ''}
      >Saltar este paso (sin sucursal por ahora)</button>
    </div>
  );
}

// ============================================
// PASO 4 — Persona de contacto
// ============================================
function Paso4Persona({ persona, setPersona, celularDuplicado, saltarPersona, setSaltarPersona, esCelular }) {
  const k = (campo) => (e) => setPersona({ ...persona, [campo]: e.target.value });
  const algoEscrito = !!(persona.nombre_completo.trim() || persona.celular.trim() || persona.email.trim());
  if (saltarPersona) {
    return (
      <div style={cajaInfo}>
        <span style={{ fontSize: 16, marginRight: 8 }}>ℹ️</span>
        Vas a registrar el cliente sin persona de contacto. Se podrá agregar después.
        {setSaltarPersona && (
          <button
            type="button"
            onClick={() => setSaltarPersona(false)}
            style={{ ...btnLink, marginLeft: 8 }}
          >Deshacer</button>
        )}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ fontSize: 13, color: COLOR_OSCURO, fontWeight: 600, margin: 0 }}>
        Persona de contacto principal
      </p>

      <div>
        <p style={labelMini}>Nombre completo *</p>
        <input
          type="text" value={persona.nombre_completo} onChange={k('nombre_completo')}
          placeholder="Ej: Juan Pérez García"
          style={inputStyle}
        />
      </div>

      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr' }}>
        <div>
          <p style={labelMini}>Celular</p>
          <input
            type="tel" value={persona.celular} onChange={k('celular')}
            placeholder="55 1234 5678" style={inputStyle}
          />
        </div>
        <div>
          <p style={labelMini}>Email</p>
          <input
            type="email" value={persona.email} onChange={k('email')}
            placeholder="contacto@empresa.com" style={inputStyle}
          />
        </div>
      </div>

      <div>
        <p style={labelMini}>Tipo de rol</p>
        <select
          value={persona.tipo_rol}
          onChange={k('tipo_rol')}
          style={inputStyle}
        >
          {TIPOS_ROL.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
        </select>
      </div>

      {celularDuplicado && (
        <div style={cajaAlerta}>
          <span style={{ fontSize: 16, marginRight: 8 }}>⚠️</span>
          Ya existe una persona con ese celular: <strong>{celularDuplicado.nombre_completo}</strong>.
          Considera buscarla en el selector principal en lugar de crear duplicada.
        </div>
      )}

      {setSaltarPersona && (
        <button
          type="button"
          onClick={() => setSaltarPersona(true)}
          disabled={algoEscrito}
          style={{
            ...btnLink, marginTop: 4, alignSelf: 'flex-start',
            opacity: algoEscrito ? 0.4 : 1,
            cursor: algoEscrito ? 'not-allowed' : 'pointer',
          }}
          title={algoEscrito ? 'Borra los campos para saltar' : ''}
        >Saltar este paso (sin persona por ahora)</button>
      )}
    </div>
  );
}

function BotonOpcion({ sel, onClick, children }) {
  return (
    <button
      type="button" onClick={onClick}
      style={{
        background: sel ? COLOR_AZUL + '14' : '#fff',
        border: `2px solid ${sel ? COLOR_AZUL : COLOR_BORDE}`,
        borderRadius: 10, padding: '12px',
        cursor: 'pointer', fontFamily: FF,
        color: sel ? COLOR_AZUL : COLOR_OSCURO,
        textAlign: 'left',
        transition: 'border-color 0.2s, background 0.2s',
      }}
    >{children}</button>
  );
}

// ============================================
// ESTILOS
// ============================================
const inputStyle = {
  width: '100%', padding: '10px 12px',
  border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
  fontSize: 14, fontFamily: FF, color: COLOR_OSCURO,
  outline: 'none', boxSizing: 'border-box', background: '#fff',
};

const labelMini = {
  fontSize: 12, color: COLOR_GRIS, fontWeight: 600, margin: '0 0 6px',
};

const cajaAlerta = {
  background: '#fff8ed', border: '1px solid #f0d9b8', color: '#a86b1e',
  padding: 10, borderRadius: 8, fontSize: 12, lineHeight: 1.5,
};

const cajaInfo = {
  background: '#eef4fc', border: '1px solid #c7daf2', color: COLOR_AZUL,
  padding: 10, borderRadius: 8, fontSize: 12,
};

const btnPrimario = {
  background: COLOR_LIMA, color: '#fff', border: 'none',
  borderRadius: 10, padding: '11px 22px', fontSize: 14,
  fontWeight: 700, fontFamily: FF, cursor: 'pointer',
  minHeight: 44,
};

const btnSecundario = {
  background: '#fff', color: COLOR_GRIS, border: `1px solid ${COLOR_BORDE}`,
  borderRadius: 10, padding: '11px 18px', fontSize: 14,
  fontWeight: 600, fontFamily: FF, cursor: 'pointer',
  minHeight: 44,
};

const btnLink = {
  background: 'transparent', border: 'none', color: COLOR_AZUL,
  fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: FF, padding: 0,
};
